import React, { useState } from 'react';
import { CompleteKundali, Language } from '../../types/kundali';
import { calculateAshtakootaSynastry } from '../../utils/v518/ashtakootaEngine';
import { 
  X, 
  Heart, 
  Sparkles, 
  ShieldCheck, 
  AlertTriangle, 
  CheckCircle2, 
  HelpCircle, 
  Printer, 
  Download,
  Info
} from 'lucide-react';

interface KundaliMilanModalProps {
  isOpen: boolean;
  onClose: () => void;
  primaryKundali: CompleteKundali;
  secondaryKundali?: CompleteKundali;
  lang?: Language;
}

export const KundaliMilanModal: React.FC<KundaliMilanModalProps> = ({
  isOpen,
  onClose,
  primaryKundali,
  secondaryKundali,
  lang = 'en'
}) => {
  if (!isOpen) return null;

  const isHi = lang === 'hi';
  const partnerB = secondaryKundali || primaryKundali; // Fallback to self-test if no partner provided

  const result = calculateAshtakootaSynastry(
    primaryKundali,
    partnerB,
    primaryKundali.birthDetails.name || (isHi ? 'प्रथम जातक' : 'Partner A'),
    partnerB.birthDetails.name || (isHi ? 'द्वितीय जातक' : 'Partner B'),
    lang
  );

  const getVerdictBadge = (verdict: typeof result.verdict) => {
    switch (verdict) {
      case 'EXCELLENT':
        return 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/30';
      case 'VERY_GOOD':
        return 'bg-blue-500/10 text-blue-700 dark:text-blue-300 border-blue-500/30';
      case 'GOOD_WITH_CARE':
        return 'bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-500/30';
      default:
        return 'bg-rose-500/10 text-rose-700 dark:text-rose-300 border-rose-500/30';
    }
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <div id="v518-kundali-milan-modal" className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-astro-primary/60 backdrop-blur-sm overflow-y-auto">
      <div className="bg-white dark:bg-astro-primary border border-astro-border dark:border-astro-strong-border rounded-2xl max-w-3xl w-full max-h-[90vh] flex flex-col shadow-2xl overflow-hidden my-auto">
        {/* Modal Header */}
        <div className="flex items-center justify-between p-5 border-b border-astro-border dark:border-astro-strong-border bg-astro-secondary-surface/50 dark:bg-astro-secondary/30">
          <div className="flex items-center gap-2.5">
            <span className="p-2 rounded-xl bg-pink-500/10 text-pink-600 dark:text-pink-400">
              <Heart className="w-5 h-5" />
            </span>
            <div>
              <h2 className="text-lg font-bold text-astro-text dark:text-white flex items-center gap-2">
                {isHi ? 'अष्टकूट गुण मिलान एवं संबंध विश्लेषण' : 'Ashtakoota Synastry & Marital Compatibility'}
                <span className="text-xs px-2 py-0.5 rounded-full bg-pink-100 dark:bg-pink-950 text-pink-700 dark:text-pink-300 font-normal">
                  36 Gunas
                </span>
              </h2>
              <p className="text-xs text-astro-muted dark:text-astro-muted">
                {result.partnerAName} ({result.partnerAMoonSign}, {result.partnerANakshatra}) & {result.partnerBName} ({result.partnerBMoonSign}, {result.partnerBNakshatra})
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              id="print-milan-btn"
              onClick={handlePrint}
              className="p-2 text-astro-muted hover:text-astro-text dark:hover:text-white rounded-lg hover:bg-astro-secondary-surface dark:hover:bg-astro-secondary transition-all"
              title={isHi ? 'प्रिंट रिपोर्ट' : 'Print Report'}
            >
              <Printer className="w-4 h-4" />
            </button>
            <button
              id="close-milan-modal-btn"
              onClick={onClose}
              className="p-2 text-astro-muted hover:text-astro-text dark:hover:text-white rounded-lg hover:bg-astro-secondary-surface dark:hover:bg-astro-secondary transition-all"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Modal Body */}
        <div className="p-5 md:p-6 overflow-y-auto space-y-6">
          {/* Score & Verdict Banner */}
          <div className="p-5 rounded-xl bg-gradient-to-r from-pink-500/5 via-purple-500/5 to-indigo-500/5 border border-pink-200/60 dark:border-pink-900/40 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <div className="text-xs text-astro-muted dark:text-astro-muted font-medium">
                {isHi ? 'कुल प्राप्त गुण एवं अनुकूलता' : 'Total Obtained Gunas & Compatibility'}
              </div>
              <div className="mt-1 flex items-baseline gap-2">
                <span className="text-3xl font-extrabold text-astro-text dark:text-white">
                  {result.totalScore}
                </span>
                <span className="text-base text-astro-muted font-medium">/ 36 Gunas</span>
                <span className="text-sm font-semibold text-pink-600 dark:text-pink-400 ml-2">
                  ({result.compatibilityPercentage}%)
                </span>
              </div>
            </div>

            <div>
              <span className={`inline-block px-3 py-1 text-xs font-bold rounded-full border ${getVerdictBadge(result.verdict)}`}>
                {isHi ? result.verdictTitleHi : result.verdictTitleEn}
              </span>
            </div>
          </div>

          {/* 8 Kootas Breakdown Table */}
          <div>
            <div className="text-xs font-bold text-astro-text dark:text-astro-muted uppercase tracking-wider mb-3">
              {isHi ? 'अष्टकूट विस्तृत विवरण (8 Classical Kootas)' : 'Ashtakoota Component Breakdown'}
            </div>

            <div className="border border-astro-border dark:border-astro-strong-border rounded-xl overflow-hidden shadow-sm">
              <table className="w-full text-left text-xs border-collapse">
                <thead className="bg-astro-secondary-surface dark:bg-astro-secondary/80 border-b border-astro-border dark:border-astro-strong-border text-astro-secondary-text dark:text-astro-muted font-semibold">
                  <tr>
                    <th className="p-3">{isHi ? 'कूट नाम' : 'Koota'}</th>
                    <th className="p-3">{isHi ? 'प्राप्तांक' : 'Points'}</th>
                    <th className="p-3">{isHi ? 'प्रथम जातक' : 'Partner A'}</th>
                    <th className="p-3">{isHi ? 'द्वितीय जातक' : 'Partner B'}</th>
                    <th className="p-3">{isHi ? 'स्थिति / परिहार' : 'Status & Mitigation'}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800 text-astro-secondary-text dark:text-astro-muted">
                  {result.components.map((comp) => (
                    <tr key={comp.gunaName} className="hover:bg-astro-secondary-surface/50 dark:hover:bg-astro-secondary/30">
                      <td className="p-3 font-semibold text-astro-text dark:text-white">
                        {isHi ? comp.gunaNameHi : comp.gunaName}
                      </td>
                      <td className="p-3 font-bold">
                        <span className={comp.obtainedPoints === comp.maxPoints ? 'text-emerald-600 dark:text-emerald-400' : comp.obtainedPoints === 0 ? 'text-rose-600 dark:text-rose-400' : 'text-astro-secondary-text dark:text-astro-muted'}>
                          {comp.obtainedPoints}
                        </span>
                        <span className="text-astro-muted font-normal"> / {comp.maxPoints}</span>
                      </td>
                      <td className="p-3 text-astro-secondary-text dark:text-astro-muted">{comp.partnerAValue}</td>
                      <td className="p-3 text-astro-secondary-text dark:text-astro-muted">{comp.partnerBValue}</td>
                      <td className="p-3">
                        {comp.isDoshaCancelled ? (
                          <div className="flex items-center gap-1 text-emerald-600 dark:text-emerald-400 font-medium">
                            <ShieldCheck className="w-3.5 h-3.5" />
                            <span>{isHi ? 'दोष परिहार मान्य' : 'Dosha Cancelled'}</span>
                          </div>
                        ) : comp.isDoshaPresent ? (
                          <div className="flex items-center gap-1 text-rose-600 dark:text-rose-400 font-medium">
                            <AlertTriangle className="w-3.5 h-3.5" />
                            <span>{comp.doshaName}</span>
                          </div>
                        ) : (
                          <span className="text-astro-muted font-normal">
                            {isHi ? comp.descriptionHi : comp.descriptionEn}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Manglik Mutual Compatibility */}
          <div className="p-4 rounded-xl bg-astro-secondary-surface dark:bg-astro-secondary/40 border border-astro-border/80 dark:border-astro-strong-border/60">
            <div className="flex items-center gap-2 mb-2 font-semibold text-xs text-astro-text dark:text-astro-muted">
              <Sparkles className="w-4 h-4 text-amber-500" />
              {isHi ? 'मांगलिक दोष एवं परस्पर संतुलन' : 'Manglik Analysis & Energetic Balance'}
            </div>
            <p className="text-xs text-astro-secondary-text dark:text-astro-muted">
              {isHi ? result.manglikAnalysis.mutualVerdictHi : result.manglikAnalysis.mutualVerdictEn}
            </p>
          </div>

          {/* Constructive Counseling Note */}
          <div className="p-4 rounded-xl bg-indigo-50/60 dark:bg-indigo-950/20 border border-indigo-100 dark:border-indigo-900/40 text-xs text-indigo-900 dark:text-indigo-200 flex items-start gap-2.5">
            <Info className="w-4 h-4 text-indigo-500 shrink-0 mt-0.5" />
            <div>
              <span className="font-semibold">{isHi ? 'सकारात्मक मार्गदर्शन: ' : 'Constructive Guidance: '}</span>
              {isHi ? result.constructiveCounselingHi : result.constructiveCounselingEn}
            </div>
          </div>
        </div>

        {/* Modal Footer */}
        <div className="p-4 border-t border-astro-border dark:border-astro-strong-border bg-astro-secondary-surface/50 dark:bg-astro-secondary/30 flex justify-end">
          <button
            id="close-milan-bottom-btn"
            onClick={onClose}
            className="px-4 py-2 bg-astro-primary text-white dark:bg-white dark:text-astro-text text-xs font-semibold rounded-lg hover:opacity-90 transition-all"
          >
            {isHi ? 'पूर्ण' : 'Close'}
          </button>
        </div>
      </div>
    </div>
  );
};
